import { useEffect, useCallback, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
    getBuildingDevice, getBuildingThemes, getIsLoadingBuildingThemes,
    HasUrlSlug, HasDeviceId, getLoadingBuildingThemesError, getBuilding,
} from 'selectors/buildingsSelectors';
import { getScheduleForDevice } from 'selectors/schedulesSelectors';
import { getBuildingThemesRequest } from 'actions/buildingActions';
import { saveScheduleSuccess } from 'actions/scheduleActions';
import { DeviceScheduleForm } from '../deviceScheduleForm/DeviceScheduleForm';
import { Loading } from 'ui/shared/loading';
import { Schedule } from 'shared/models';
import { State } from 'reducers';
import { useRouteMatch } from 'react-router';
import { toError } from 'shared/util/toError';
import { useTranslation } from 'react-i18next';
import { dehumanizeScheduleRule, humanizeScheduleRule, ScheduleRule } from 'logic/scheduleRule';
import { callApi } from 'apiClient';
import { useDeviceTypes } from 'hooks/useDeviceTypes/useDeviceTypes';
import { ErrorMessage, SuccessMessage } from 'ui/shared/messages';
import { useBuilding } from 'hooks/useBuilding/useBuilding';
import { useSession } from 'hooks';
import { Permission } from 'shared/permissions';

require('./DeviceSchedulePage.css');

export const DeviceSchedulePage = () => {
    const { t } = useTranslation();
    const { hasPermission, user } = useSession();
    const routeMatch = useRouteMatch<HasUrlSlug & HasDeviceId>('/buildings/:urlSlug/devices/:deviceId');
    const deviceId = routeMatch!.params.deviceId;

    const building = useSelector((state: State) => routeMatch ? getBuilding(state, routeMatch.params) : null);
    const buildingId = building && building.id;

    const { infotainmentOrg } = useBuilding(buildingId, true);
    const canEdit = infotainmentOrg
        ? (hasPermission(null, Permission.Admin) || user?.orgId === infotainmentOrg?.orgId)
        : (hasPermission(buildingId, Permission.ViewDevices) && hasPermission(buildingId, Permission.ViewThemes));

    const { deviceTypes } = useDeviceTypes();

    const device = useSelector((state: State) => routeMatch ? getBuildingDevice(state, routeMatch.params) : null);
    const isPI = !!(device && device.properties.pi);

    const deviceType = device && deviceTypes[device.deviceTypeId];

    const [ saving, setSaving ] = useState(false);
    const [ errorMessage, setErrorMessage ] = useState('');
    const [ successVisible, setSuccessVisible ] = useState(false);

    const emptySchedule = useMemo(() => ({
        deviceId,
        rules: [],
    }), [ deviceId ]);

    const schedule = useSelector((state: State) => (routeMatch && getScheduleForDevice(state, routeMatch.params)) || emptySchedule);
    const themes = useSelector((state: State) => buildingId ? getBuildingThemes(state, { buildingId }) : {});

    const isLoadingThemes = useSelector((state: State) => buildingId ? getIsLoadingBuildingThemes(state, { buildingId }) : false);

    const themesError = useSelector((state: State) => buildingId ? getLoadingBuildingThemesError(state, { buildingId }) : null);

    const dispatch = useDispatch();

    const handleSave = useCallback((schedule: Schedule) => {
        setErrorMessage('');
        setSuccessVisible(false);
        setSaving(true);
        void (async () => {
            try {
                if (!buildingId || !deviceId) return;
                const { timeZone } = building!;
        
                // Convert the string times back into numbers (number of minutes)
                const scheduleData = {
                    ...schedule,
                    rules: schedule.rules.map(rule => dehumanizeScheduleRule(ScheduleRule.fromModel(rule), timeZone)),
                };
    
                schedule = await callApi('PUT', `/api/buildings/${buildingId}/devices/${deviceId}/schedule`, scheduleData);
        
                // Convert from numbers to strings
                schedule.rules = schedule.rules.map(rule => humanizeScheduleRule(rule, timeZone));
        
                dispatch(saveScheduleSuccess(schedule, { buildingId, deviceId }));
                setSuccessVisible(true);
            }
            catch (e) {
                const err = toError(e);
                setErrorMessage(t([ `ERROR.${err.message}`, 'SERVER_ERROR' ]));
            }
            finally {
                setSaving(false);
            }
        })();
    }, [ dispatch, building, buildingId, deviceId, setSaving, setErrorMessage, t ]);

    // Load themes if they aren't loaded yet and we haven't started loading them already
    useEffect(() => {
        if (buildingId && !isLoadingThemes && !themes && !themesError) {
            dispatch(getBuildingThemesRequest(buildingId));
        }
    }, [ dispatch, isLoadingThemes, themes, themesError, buildingId ]);

    return (
        <div id="device-schedule-page" className="page">
            { buildingId && deviceType && themes
                ? <DeviceScheduleForm
                    infotainmentOrg={ infotainmentOrg }
                    canEdit={ canEdit }
                    buildingId={ buildingId }
                    deviceType={ deviceType }
                    isPI={ isPI }
                    saving={ saving }
                    schedule={ schedule }
                    onSave={ handleSave }
                />
                : <Loading/>
            }
            <ErrorMessage message={ errorMessage }/>
            { successVisible &&
                <SuccessMessage
                    hideAfter={ 4000 }
                    message={ t('SAVED_MESSAGE') }
                    onClick={ () => setSuccessVisible(false) }
                />
            }
        </div>
    );
};
